/**
 * First-use intro shown when Review Mode ("Get Support") is activated —
 * a 20-second orientation with a "don't show again" checkbox persisted per
 * browser. Marked data-review-overlay so it can never end up in a capture.
 */
import React from 'react';
import {
  ClipboardEdit,
  EyeOff,
  MousePointerClick,
  PenLine,
  StickyNote,
  X,
} from 'lucide-react';

export const REVIEW_INTRO_DISMISSED_KEY = 'pc.review.introDismissed';

export function isReviewIntroDismissed(): boolean {
  try {
    return localStorage.getItem(REVIEW_INTRO_DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

interface Props {
  onClose: () => void;
}

export const ReviewIntro: React.FC<Props> = ({ onClose }) => {
  const [dontShowAgain, setDontShowAgain] = React.useState(false);

  const close = (): void => {
    if (dontShowAgain) {
      try {
        localStorage.setItem(REVIEW_INTRO_DISMISSED_KEY, '1');
      } catch {
        // storage unavailable — just close
      }
    }
    onClose();
  };

  const step = (
    icon: React.ElementType,
    title: string,
    body: string,
  ): React.ReactElement => {
    const Icon = icon;
    return (
      <li className="flex items-start gap-3">
        <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-rose-100">
          <Icon className="h-4 w-4 text-rose-600" />
        </span>
        <span>
          <span className="block text-sm font-semibold text-neutral-900">{title}</span>
          <span className="block text-xs leading-relaxed text-neutral-600">{body}</span>
        </span>
      </li>
    );
  };

  return (
    <div
      data-review-overlay=""
      className="fixed inset-0 z-[10312] flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="How to use Get Support"
    >
      <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <h2 className="flex items-center gap-2 text-lg font-bold text-neutral-900">
              <ClipboardEdit className="h-5 w-5 text-rose-600" />
              Show us the problem
            </h2>
            <p className="text-xs text-neutral-500">
              Mark up this screen and support sees exactly what you see.
            </p>
          </div>
          <button
            type="button"
            onClick={close}
            aria-label="Close intro"
            className="rounded-full p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <ol className="space-y-3">
          {step(
            PenLine,
            '1. Pick a tool from the top toolbar',
            'Pin, arrow, box, freehand or a text label — then draw right on the screen.',
          )}
          {step(
            MousePointerClick,
            '2. No tool selected? Keep working',
            'The app stays fully clickable — you only "pick up a pen" when you choose a tool. You can navigate to the exact screen first.',
          )}
          {step(
            EyeOff,
            '3. Hide anything private',
            'The redact tool blacks out sensitive info — permanently, including in the screenshot we save.',
          )}
          {step(
            StickyNote,
            '4. Hit "Save note"',
            'We capture a screenshot, you add a short note, and the support team is notified immediately.',
          )}
        </ol>

        <div className="mt-4 flex items-center justify-between gap-3 border-t border-neutral-100 pt-3">
          <label className="flex items-center gap-2 text-xs text-neutral-600">
            <input
              type="checkbox"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
            />
            Don't show this again
          </label>
          <button type="button" onClick={close} className="primary-button text-sm">
            Got it — start marking up
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReviewIntro;
